/**
 * Fantasy Game Repository
 * Service layer for personal FPL Entry data
 * Fetches from FPL API and converts to domain models
 */

import {
  FplClient,
  type EntryData,
  type EntryPicksData,
  type LeagueStandingsData,
} from '@shared/services/fpl-client';
import { FantasyGameLiveService } from '@shared/services/fantasy-game-live.service';
import type {
  FantasyEntry,
  FantasyManager,
  FantasyTeam,
  FantasyGameweekHistory,
  FantasyGameweekPicks,
  FantasyPick,
  FantasyLeagueStanding,
  FantasyLeagueStandings,
  LiveSquadPerformance,
} from '@domain/models';

export class FantasyGameRepository {
  private fplClient: FplClient;
  private liveService: FantasyGameLiveService;
  private inFlightRequests: Map<string, Promise<unknown>> = new Map();

  constructor() {
    this.fplClient = new FplClient();
    this.liveService = new FantasyGameLiveService();
  }

  /**
   * Deduplicate concurrent requests with identical keys
   * Returns existing in-flight Promise if request already started
   * Automatically cleans up completed requests from cache
   */
  private withDeduplication<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const cached = this.inFlightRequests.get(key);
    if (cached) {
      return cached as Promise<T>;
    }

    const promise = fetcher().finally(() => {
      // Clean up on completion (success or error)
      this.inFlightRequests.delete(key);
    });

    this.inFlightRequests.set(key, promise);
    return promise;
  }

  /**
   * Get entry profile and information
   */
  async getEntry(entryId: number): Promise<FantasyEntry> {
    return this.withDeduplication(`entry-${entryId}`, async () => {
      const data = await this.fplClient.getEntry(entryId);
      return this.mapEntryToModel(data);
    });
  }

  /**
   * Get entry gameweek history
   */
  async getEntryHistory(entryId: number): Promise<FantasyGameweekHistory[]> {
    return this.withDeduplication(`entry-history-${entryId}`, async () => {
      const data = await this.fplClient.getEntryHistory(entryId);
      return data.current.map((h) => ({
        event: h.event,
        points: h.points,
        totalPoints: h.total_points,
        rank: h.rank,
        prevRank: h.rank_sort,
        rankSort: h.rank_sort,
        transfers: h.transfers_made,
        transfersCost: h.transfers_cost,
        benchPoints: h.points_on_bench ?? 0,
        bankValue: h.bank,
        teamValue: h.value,
        eventTransfers: h.event_transfers,
        eventTransfersCost: h.event_transfers_cost,
      }));
    });
  }

  /**
   * Get gameweek picks for entry
   */
  async getEntryPicks(entryId: number, eventId: number): Promise<FantasyGameweekPicks> {
    return this.withDeduplication(`entry-picks-${entryId}-${eventId}`, async () => {
      try {
        const data = await this.fplClient.getEntryPicks(entryId, eventId);
        return this.mapPicksToModel(data, entryId, eventId);
      } catch (publicPicksError) {
        try {
          const currentTeam = await this.fplClient.getCurrentTeam(entryId);
          return {
            eventId,
            entryId,
            picks: currentTeam.picks.map((pick) => ({
              element: pick.element,
              position: pick.position,
              multiplier: pick.multiplier,
              isCaptain: pick.is_captain,
              isViceCaptain: pick.is_vice_captain,
              isBench: pick.position > 11,
              benchOrder: pick.position > 11 ? pick.position - 11 : undefined,
            })),
            transfersMade: currentTeam.transfers?.made ?? 0,
            transfersCost: currentTeam.transfers?.cost ?? 0,
            bankValue: currentTeam.transfers?.bank ?? 0,
            teamValue: currentTeam.transfers?.value ?? 0,
            status: 'pre-season',
            activeChip: null,
            autoSubs: [],
          };
        } catch {
          throw publicPicksError;
        }
      }
    });
  }

  /**
   * Get league standings with pagination support
   */
  async getLeagueStandings(leagueId: number, page?: number): Promise<FantasyLeagueStandings> {
    return this.withDeduplication(`league-standings-${leagueId}-${page || 1}`, async () => {
      const data = await this.fplClient.getLeagueStandings(leagueId, page);
      return this.mapLeagueStandingsToModel(data);
    });
  }

  /**
   * Get all leagues for a connected entry
   * Fetches league info for each joined league
   */
  async getEntryLeagues(entryId: number): Promise<Array<{ id: number; name: string }>> {
    try {
      const entry = await this.getEntry(entryId);
      if (entry.joinedLeagues.length > 0) {
        return entry.joinedLeagues.map(({ id, name }) => ({ id, name }));
      }
      const leagues = await Promise.all(
        entry.joinedLeaguesIds.map(async (leagueId) => {
          try {
            const standings = await this.fplClient.getLeagueStandings(leagueId, 1);
            return {
              id: leagueId,
              name: standings.league.name,
            };
          } catch (err) {
            // Fallback if league data fails
            return {
              id: leagueId,
              name: `League ${leagueId}`,
            };
          }
        })
      );
      return leagues;
    } catch (err) {
      throw new Error(
        `Failed to get entry leagues: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get live squad performance for a gameweek
   * Merges picks with live event data
   */
  async getLiveSquadPerformance(
    entryId: number,
    gameweekId: number,
    playerMap?: Map<number, any>
  ): Promise<LiveSquadPerformance> {
    // Fetch picks and live data in parallel
    const [picks, eventLiveData] = await Promise.all([
      this.getEntryPicks(entryId, gameweekId),
      this.fplClient.getEventLive(gameweekId),
    ]);

    // Calculate squad performance
    return this.liveService.calculateSquadPerformance(
      entryId,
      gameweekId,
      picks,
      eventLiveData,
      playerMap
    );
  }

  // Mappers

  private mapEntryToModel(data: EntryData): FantasyEntry {
    // Defensive: Handle cases where manager object might be missing or incomplete
    const managerName = data.manager?.name || `${data.player_first_name} ${data.player_last_name}`;
    const joinDate = data.manager?.join_date;

    if (!managerName || managerName.trim().length === 0) {
      throw new Error(`Failed to map entry data: Manager name not found. Entry ID: ${data.id}`);
    }

    const manager: FantasyManager = {
      id: data.id,
      name: managerName,
      totalPoints: data.summary_overall_points,
      overallRank: data.summary_overall_rank,
      region: data.player_region_name,
      joinedDate: joinDate,
    };

    const team: FantasyTeam = {
      id: data.id,
      entryId: data.id,
      name: data.name || 'Team',
      transfersMade: 0,
      transfersBudget: 0,
    };

    const joinedLeagues = data.leagues?.classic?.map((league) => ({
      id: league.id,
      name: league.name,
      rank: league.rank ?? null,
    })) || [];

    return {
      id: data.id,
      manager,
      team,
      joinedLeaguesIds: joinedLeagues.map((league) => league.id),
      joinedLeagues,
    };
  }

  private mapPicksToModel(
    data: EntryPicksData,
    entryId: number,
    eventId: number
  ): FantasyGameweekPicks {
    const picks: FantasyPick[] = data.picks.map((pick) => ({
      element: pick.element,
      position: pick.position,
      multiplier: pick.multiplier,
      isCaptain: pick.is_captain,
      isViceCaptain: pick.is_vice_captain,
      isBench: pick.position > 11,
      benchOrder: pick.position > 11 ? pick.position - 11 : undefined,
    }));

    return {
      eventId,
      entryId,
      picks,
      transfersMade: data.entry_history.event_transfers,
      transfersCost: data.entry_history.event_transfers_cost,
      bankValue: data.entry_history.bank,
      teamValue: data.entry_history.value,
      status: 'active',
      activeChip: data.active_chip,
      autoSubs: data.automatic_subs.map((sub) => ({
        elementIn: sub.element_in,
        elementOut: sub.element_out,
        subOrder: sub.sub_order,
      })),
    };
  }

  private mapLeagueStandingsToModel(data: LeagueStandingsData): FantasyLeagueStandings {
    const rankedResults = data.standings.results;
    const pendingResults = data.new_entries?.results ?? [];
    const standings: FantasyLeagueStanding[] = rankedResults.map((result) => ({
      rank: result.rank,
      prevRank: result.previous_rank,
      entryId: result.entry,
      entryName: result.entry_name,
      playerName: result.player_name,
      teamName: result.entry_name,
      points: result.total,
      eventPoints: result.event_total,
      totalPoints: result.total,
      lastRank: result.last_rank || undefined,
    }));
    if (standings.length === 0 && pendingResults.length > 0) {
      standings.push(
        ...pendingResults.map((result, index) => ({
          rank: index + 1,
          prevRank: null,
          entryId: result.entry,
          entryName: result.entry_name,
          playerName:
            result.player_name ||
            [result.player_first_name, result.player_last_name].filter(Boolean).join(' ') ||
            'Manager',
          teamName: result.entry_name,
          points: 0,
          eventPoints: 0,
          totalPoints: 0,
        }))
      );
    }

    return {
      leagueId: data.league.id,
      leagueName: data.league.name,
      standings,
      pageStandings: standings,
      leagueType: data.league.leagueType || 'classic',
      results: standings,
      hasNext:
        rankedResults.length > 0
          ? data.standings.has_next
          : Boolean(data.new_entries?.has_next),
      pageNumber:
        rankedResults.length > 0 ? data.standings.page : (data.new_entries?.page ?? 1),
      pageSize: standings.length,
    };
  }
}
